import { useNavigate } from "react-router-dom";
import { useBooking } from "../context/BookingContext";
import {
  CardContent,
  Typography,
  Button,
  Container,
  Box,
  Grid,
  Divider,
  Link,
  Paper,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  DialogContentText,
  Alert,
} from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import EventIcon from "@mui/icons-material/Event";
import HomeIcon from "@mui/icons-material/Home";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DescriptionIcon from "@mui/icons-material/Description";
import DownloadIcon from "@mui/icons-material/Download";
import { useEffect, useState } from "react";
import WaiverForm from "../assets/Waiver-form.pdf";
import { AccessTime } from "@mui/icons-material";

const ConfirmBookingPage = () => {
  const token = localStorage.getItem("token");
  const [open, setOpen] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [popupMessage] = useState("Please log in to confirm your booking.");
  const [currentTimeRemaining, setCurrentTimeRemaining] = useState<number>(0);
  const {
    timeRemaining,
    confirmBooking,
    selectedListing,
    selectedDate,
    startTime,
    endTime,
  } = useBooking();
  const navigate = useNavigate();
  // Format date for better readability
  const formattedDate = selectedDate
    ? new Date(selectedDate).toLocaleDateString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "Not selected";

  const handleConfirm = async () => {
    if (!token) {
      setOpen(true);
      return;
    }

    if (!agreeToTerms) {
      alert("You must agree to the terms and conditions to proceed.");
      return;
    }

    const response = await confirmBooking();
    if (response.url) {
      window.location.href = response.url;
    } else if (response === false) {
      setOpenConfirm(true);
    }
  };

  const handleClose = () => {
    setOpen(false);
    if (popupMessage.includes("log in")) {
      navigate(`/login`);
    }
  };
  const handleCloseConfirm = () => {
    setOpenConfirm(false);
  };

  const handleBack = () => {
    navigate(-1);
  };

  useEffect(() => {
    // Calculate initial remaining time from the timestamp
    const remainingMs = calculateTimeRemaining(timeRemaining);
    setCurrentTimeRemaining(remainingMs);

    if (remainingMs <= 0) {
      // setIsExpired(true);
      return;
    }

    const timer = setInterval(() => {
      const currentRemaining = calculateTimeRemaining(timeRemaining);
      setCurrentTimeRemaining(currentRemaining); // Update state to trigger re-render
      if (currentRemaining <= 0) {
        // setIsExpired(true);
        clearInterval(timer);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [timeRemaining]);

  // Function to calculate remaining time from timestamp
  const calculateTimeRemaining = (expiryTimestamp: string | number) => {
    const now = new Date().getTime();
    const expiry = new Date(expiryTimestamp).getTime();
    return Math.max(0, expiry - now);
  };

  // Fixed formatTime function
  const formatTime = (milliseconds: number) => {
    // Handle the case where milliseconds might be undefined or NaN
    if (!milliseconds || isNaN(milliseconds)) {
      return "0:00";
    }

    const totalSeconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  if (!selectedListing) {
    return (
      <Container maxWidth="sm" sx={{ mt: 4 }}>
        <Paper elevation={3} sx={{ p: 3, textAlign: "center" }}>
          <Typography variant="h5" gutterBottom>
            No Listing Selected
          </Typography>
          <Typography variant="body1" paragraph>
            Please go back and select a listing before confirming your booking.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate("/listings")}
            startIcon={<ArrowBackIcon />}
          >
            Return to Listings
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ mt: 4, mb: 4 }}>
      <Button startIcon={<ArrowBackIcon />} onClick={handleBack} sx={{ mb: 2 }}>
        Back
      </Button>

      <Alert
        severity="warning"
        icon={<AccessTime />}
        sx={{
          backgroundColor: "#fff7ed", // orange-50 equivalent
          borderLeft: "4px solid #fb923c", // orange-400 equivalent
          border: "none",
          borderRadius: 1,
          "& .MuiAlert-icon": {
            color: "#fb923c", // orange-400
            marginRight: 1.5,
          },
        }}
      >
        <Box>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              color: "#9a3412", // orange-800 equivalent
              mb: 0.5,
            }}
          >
            Your slot is temporarily reserved
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "#c2410c" }} // orange-700 equivalent
          >
            Complete payment within:{" "}
            <Typography
              component="span"
              sx={{
                fontWeight: "bold",
                fontSize: "1.125rem", // text-lg equivalent
                color: "#c2410c",
              }}
            >
              {formatTime(currentTimeRemaining)} minutes
            </Typography>
          </Typography>
        </Box>
      </Alert>

      <Paper elevation={3}>
        <Box
          sx={{ p: 2, bgcolor: "primary.main", color: "primary.contrastText" }}
        >
          <Typography variant="h5" align="center">
            Confirm Your Booking
          </Typography>
        </Box>

        <CardContent>
          <Stack spacing={3}>
            <Box>
              <Box display="flex" alignItems="center" mb={1}>
                <HomeIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="subtitle1">Listing</Typography>
              </Box>
              <Typography variant="h6" fontWeight="bold">
                {selectedListing?.name || "Not selected"}
              </Typography>
              {selectedListing?.address && (
                <Typography variant="body2" color="text.secondary">
                  {selectedListing.address}
                </Typography>
              )}
            </Box>

            <Divider />

            <Box>
              <Box display="flex" alignItems="center" mb={1}>
                <EventIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="subtitle1">Date</Typography>
              </Box>
              <Typography variant="body1">{formattedDate}</Typography>
            </Box>

            <Divider />

            <Box>
              <Box display="flex" alignItems="center" mb={1}>
                <AccessTimeIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="subtitle1">Time</Typography>
              </Box>
              <Typography variant="body1">
                {startTime || "Not selected"} - {endTime || "Not selected"}
              </Typography>
            </Box>

            {selectedListing?.price && (
              <>
                <Divider />
                <Box>
                  <Typography variant="subtitle1" gutterBottom>
                    Price
                  </Typography>
                  <Grid container justifyContent="space-between">
                    <Typography variant="body1">Hourly Rate:</Typography>
                    <Typography variant="body1" fontWeight="bold">
                      ${selectedListing.price}/hour
                    </Typography>
                  </Grid>
                </Box>
              </>
            )}
          </Stack>

          {/* Terms and conditions section with document viewing and download options */}
          <Divider sx={{ my: 3 }} />
          <Box>
            <Box display="flex" alignItems="center" mb={1}>
              <DescriptionIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="subtitle1">Terms and Conditions</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" mb={2}>
              By confirming your booking, you agree to our terms and conditions
              mentioned in the Waiver Form. Please read them carefully before
              proceeding. We reserve the right to cancel your booking if you are
              unable to make to the venue on time.
            </Typography>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<DownloadIcon />}
              >
                <Link
                  color="inherit"
                  download
                  href={WaiverForm}
                  underline="hover"
                  display="block"
                >
                  Download Waiver
                </Link>
              </Button>
            </Box>

            <FormControlLabel
              control={
                <Checkbox
                  checked={agreeToTerms}
                  onChange={(e) => setAgreeToTerms(e.target.checked)}
                  color="primary"
                  required
                />
              }
              label="I agree to the terms and conditions"
            />
          </Box>

          <Box sx={{ mt: 4, display: "flex", justifyContent: "space-between" }}>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleBack}
              startIcon={<ArrowBackIcon />}
            >
              Go Back
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleConfirm}
              size="large"
              disabled={!agreeToTerms}
            >
              Confirm Booking
            </Button>
          </Box>
        </CardContent>
      </Paper>

      <Dialog
        open={openConfirm}
        onClose={handleCloseConfirm}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Time Slot Unavailable</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            This time slot is no longer available as it has been booked by
            someone else. Please choose a different time.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirm} autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Login Dialog */}
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>Booking Alert</DialogTitle>
        <DialogContent>
          <Typography>{popupMessage}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ConfirmBookingPage;
